import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { registryAPI, clustersAPI } from '../../services/api';

const SAMPLE_DOCKERFILE = `# Multi-stage React app → nginx (edit for your app)
FROM node:20-alpine AS build
WORKDIR /app
COPY package*.json ./
RUN npm ci
COPY . .
RUN npm run build

FROM nginx:alpine
COPY --from=build /app/build /usr/share/nginx/html
EXPOSE 80
CMD ["nginx", "-g", "daemon off;"]
`;

const label = (text, hint) => (
  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 5 }}>
    {text}{hint && <span style={{ fontWeight: 400, color: 'var(--muted-2)', marginLeft: 6 }}>{hint}</span>}
  </label>
);

export default function BuildImagePage() {
  const navigate = useNavigate();
  const [clusters, setClusters] = useState([]);
  const [form, setForm] = useState({ cluster_id: '', image: '', dockerfile: SAMPLE_DOCKERFILE });
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState('');

  // Build tracking
  const [buildName, setBuildName] = useState('');
  const [phase, setPhase] = useState('');     // pending|building|succeeded|failed
  const [logs, setLogs] = useState('');
  const logsRef = useRef();
  const pollRef = useRef();

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    clustersAPI.list()
      .then(r => {
        const cl = r.data || [];
        setClusters(cl);
        if (cl.length) setForm(f => ({ ...f, cluster_id: cl[0].id }));
      })
      .catch(() => {});
  }, []);

  // Poll status + logs while a build is active
  const poll = useCallback(async (name, clusterId) => {
    try {
      const [st, lg] = await Promise.all([
        registryAPI.buildStatus(name, clusterId),
        registryAPI.buildLogs(name, clusterId, 400),
      ]);
      setPhase(st.data.phase);
      setLogs(lg.data.logs || '');
      if (st.data.phase === 'succeeded' || st.data.phase === 'failed') {
        clearInterval(pollRef.current);
        setBuilding(false);
      }
    } catch (e) { /* transient — keep polling */ }
  }, []);

  useEffect(() => () => clearInterval(pollRef.current), []);
  useEffect(() => { if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight; }, [logs]);

  const handleBuild = async () => {
    setError('');
    if (!form.cluster_id) { setError('Select a target cluster'); return; }
    if (!/^[a-z0-9]([a-z0-9._/-]*[a-z0-9])?:[a-zA-Z0-9._-]+$/.test(form.image)) {
      setError('Image must be like "myapp/web:v1" (lowercase, include a :tag, no registry host)'); return;
    }
    if (!/FROM /i.test(form.dockerfile)) { setError('Dockerfile must contain a FROM instruction'); return; }
    setBuilding(true); setPhase('pending'); setLogs(''); setBuildName('');
    try {
      const r = await registryAPI.build({
        image: form.image,
        dockerfile: form.dockerfile,
        cluster_id: parseInt(form.cluster_id),
      });
      const name = r.data.build_name;
      setBuildName(name);
      setPhase('building');
      // start polling
      clearInterval(pollRef.current);
      pollRef.current = setInterval(() => poll(name, parseInt(form.cluster_id)), 3000);
      poll(name, parseInt(form.cluster_id));
    } catch (e) {
      setError(e.response?.data?.detail || 'Build failed to start');
      setBuilding(false); setPhase('failed');
    }
  };

  const phaseColor = phase === 'succeeded' ? 'var(--color-success-500)'
    : phase === 'failed' ? 'var(--color-danger-500)'
    : phase ? 'var(--color-warning-600, #b45309)' : 'var(--muted)';

  return (
    <div style={{ padding: '24px 32px', maxWidth: 820 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <button onClick={() => navigate('/compute/images')}
          style={{ background: 'none', border: 'none', color: 'var(--brand)', fontSize: 13, cursor: 'pointer', padding: '4px 0', fontWeight: 500 }}>
          ← Images
        </button>
        <span style={{ color: 'var(--muted-2)' }}>›</span>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>Build Image</h1>
      </div>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--muted)' }}>
        Paste a Dockerfile — it builds in-cluster (Kaniko, no Docker needed) and pushes to the
        internal registry. The image then appears in the instance image selector.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <section className="card">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              {label('Target Cluster', '* (where the build runs)')}
              <select className="select" value={form.cluster_id} onChange={e => setF('cluster_id', e.target.value)} disabled={building}>
                <option value="">— Select —</option>
                {clusters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              {label('Image name : tag', '* e.g. myapp/web:v1')}
              <input className="input" placeholder="myapp/web:v1" value={form.image}
                onChange={e => setF('image', e.target.value.toLowerCase())} disabled={building} />
            </div>
          </div>
        </section>

        <section className="card">
          {label('Dockerfile', '*')}
          <textarea className="textarea" value={form.dockerfile}
            onChange={e => setF('dockerfile', e.target.value)} disabled={building}
            style={{ width: '100%', minHeight: 260, fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.5 }} />
        </section>

        {error && <div className="banner danger">{error}</div>}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={handleBuild} disabled={building} className="btn btn-primary">
            {building ? '⏳ Building…' : '🔨 Build & Push'}
          </button>
          {phase && (
            <span style={{ fontSize: 13, fontWeight: 600, color: phaseColor }}>
              {phase === 'succeeded' ? '✓ Build succeeded — image pushed' :
               phase === 'failed' ? '✗ Build failed — see logs' :
               phase === 'building' ? '● Building…' : '○ ' + phase}
              {buildName && <span style={{ color: 'var(--muted-2)', fontWeight: 400, marginLeft: 8 }}>({buildName})</span>}
            </span>
          )}
        </div>

        {/* Build logs */}
        {(logs || building) && (
          <section className="card">
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
              Build Logs
            </div>
            <pre ref={logsRef} style={{
              margin: 0, maxHeight: 320, overflowY: 'auto', fontFamily: 'monospace', fontSize: 11.5,
              background: 'var(--surface-2)', color: 'var(--ink)', padding: 12, borderRadius: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {logs || 'Waiting for build to start…'}
            </pre>
          </section>
        )}
      </div>
    </div>
  );
}
